import { useMemo } from 'react';
import {
  DetailedShift,
  Event,
  EventInput,
  EventStatus,
  GetEventDetailsByEventIdCommandOutput,
  Shift,
  ShiftInput,
  ShiftType,
} from '@amzn/red-velvet-api';
import { useAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';
import { mapEventToEventInput, mapShiftToShiftInput } from '../../utils/eventUtils';

const isEditableShift = (shift: Shift): boolean =>
  shift.shiftType !== ShiftType.WAITLIST && shift.shiftType !== ShiftType.DROP_IN;

type EventDetailItem =
  | { kind: 'event'; event: Event }
  | { kind: 'shift'; detailedShift: DetailedShift };

interface UseEventPrePopulationResult {
  eventInput: EventInput | null;
  shiftInputs: ShiftInput[];
  shiftIdsWithSignups: Set<string>;
  totalSignupCount: number;
  hasPastShiftsWithAttendance: boolean;
  eventStatus: EventStatus | undefined;
  earliestShiftIdWithSignups: string | undefined;
  isLoading: boolean;
  error: string | null;
}

export function useEventPrePopulation(eventId: string | null): UseEventPrePopulationResult {
  const enabled = !!eventId;
  const id = eventId ?? '';

  const {
    data: items,
    isPending: isShiftsPending,
    error: shiftsError,
  } = useAllPagesRedVelvetQuery({
    enabled,
    redVelvetQueryKey: ['getEventDetailsByEventId', { eventId: id, pageSize: 25 }],
    converter: (
      output: GetEventDetailsByEventIdCommandOutput
    ): {
      items: EventDetailItem[];
      next?: string;
    } => ({
      items: [
        ...(output.event ? [{ kind: 'event' as const, event: output.event }] : []),
        ...(output.detailedShifts ?? []).map((ds) => ({
          kind: 'shift' as const,
          detailedShift: ds,
        })),
      ],
      next: output.page,
    }),
  });

  const event = useMemo(
    () =>
      (items ?? []).find(
        (i): i is Extract<EventDetailItem, { kind: 'event' }> => i.kind === 'event'
      )?.event ?? null,
    [items]
  );

  const detailedShifts = useMemo(
    () =>
      (items ?? [])
        .filter((i): i is Extract<EventDetailItem, { kind: 'shift' }> => i.kind === 'shift')
        .map((i) => i.detailedShift),
    [items]
  );

  const shiftInputs = useMemo(
    () =>
      detailedShifts
        .filter((ds) => isEditableShift(ds.shift))
        .map((ds) => mapShiftToShiftInput(ds.shift)),
    [detailedShifts]
  );

  const shiftIdsWithSignups = useMemo(
    () =>
      new Set(
        detailedShifts.filter((ds) => (ds.signups?.length ?? 0) > 0).map((ds) => ds.shift.shiftId)
      ),
    [detailedShifts]
  );

  const totalSignupCount = useMemo(
    () =>
      detailedShifts
        .filter((ds) => isEditableShift(ds.shift))
        .reduce((sum, ds) => sum + (ds.signups?.length ?? 0), 0),
    [detailedShifts]
  );

  const eventInput = useMemo(() => (event ? mapEventToEventInput(event) : null), [event]);

  const hasPastShiftsWithAttendance = useMemo(() => {
    const now = new Date();
    return detailedShifts.some(
      (ds) =>
        ds.shift.startDateTime &&
        new Date(ds.shift.startDateTime) < now &&
        (ds.attendees?.length ?? 0) > 0
    );
  }, [detailedShifts]);

  const earliestShiftIdWithSignups = useMemo(() => {
    const withSignups = detailedShifts.filter(
      (ds) => isEditableShift(ds.shift) && (ds.signups?.length ?? 0) > 0
    );
    if (withSignups.length === 0) return undefined;
    return withSignups.sort(
      (a, b) =>
        new Date(a.shift.startDateTime ?? 0).getTime() -
        new Date(b.shift.startDateTime ?? 0).getTime()
    )[0].shift.shiftId;
  }, [detailedShifts]);

  return {
    eventInput,
    shiftInputs,
    shiftIdsWithSignups,
    totalSignupCount,
    hasPastShiftsWithAttendance,
    eventStatus: event?.status,
    earliestShiftIdWithSignups,
    isLoading: isShiftsPending,
    error: shiftsError?.message ?? null,
  };
}
